<template>
    <section
        id="tab-search"
        class="section h-offset"
        v-if="listings"
        :class="{ 'map-enabled': showMap, hirer: $store.getters['user/isHirer'] }"
    >
        <div
            class="inner container"
            :class="{ 'has-selection': selected.length }"
        >
            <vue-simplebar>
                <div>
                    <!-- I am entirely necessary otherwise Vue gets confused when the pagination disappears inside a direct simplebar container, do not delete me -->
                    <div class="search-results-container">
                        <ul
                            class="listing search-results explore-results"
                            v-if="listings && listings.data.length"
                        >
                            <space
                                v-for="listing in listings.data"
                                :key="listing.id"
                                class="interactive"
                                :listing="listing"
                                :selected="isSelected(listing)"
                                @selected="select(listing)"
                                @deselected="deselect(listing)"
                            ></space>
                        </ul>
                        <div v-else>
                            <div class="alert">
                                <p>
                                    No spaces were found, please adjust your
                                    search and try again.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="pagination" v-if="listings.data.length">
                        <pagination
                            :current-page="listings.current_page"
                            :total-pages="listings.last_page"
                            :link-template="currentPath + '?page={page}'"
                            @change-page="navigate"
                        ></pagination>
                    </div>
                </div>
            </vue-simplebar>
        </div>
        <div class="map-display">
            <div class="gmap" ref="gmap"></div>
        </div>

        <div
            class="search-results-actions"
            v-sticky-top="{
                mode: 'fixed',
                offsets: ['#header'],
                vLimit: '#tab-search',
            }"
            v-if="listings.data.length"
        >
            <div class="inner">
                <div class="container">
                    <div class="result-count">
                        Found {{ listings.total }} Space{{
                            listings.total !== 1 ? "s" : ""
                        }}
                    </div>
                    <div class="actions" v-if="!$store.getters['user/isLister']">
                        <div class="action select-all">
                            <button
                                type="button"
                                class="button outline-black sm wt-md select-results"
                                @click="selectAllResults"
                            >
                                <span class="mobile-hidden uc"
                                    >Select {{ listings.total }} Results</span
                                ><span class="mobile-only inline-block uc"
                                    >Select all</span
                                >
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Space from "./shared/Space";

export default {
    components: {
        Space,
    },
    props: {
        currentPath: {
            type: String,
            default: "/explore",
        },
        listings: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            map: {
                googleMap: null,
                markers: [],
                infoWindows: [],
            },

            toggledInfowindow: false,
            addingToShortlist: false,
            selected: [],
        };
    },
    computed: {
        infoWindows() {
            return this.$store.getters["listings/allInfoWindows"];
        },
        isHirer() {
            return this.$store.getters["user/isHirer"];
        },
        isLoggedIn() {
            return this.$store.getters["user/account"];
        },
        markers() {
            return this.$store.getters["listings/allMarkers"];
        },
        showMap() {
            return this.$store.getters["listings/showMap"];
        },
    },
    mounted() {
        window.addEventListener("click", this.delegatedHideInfowindows);
        this.updateGoogleMap();


        const currentPathParams = this.currentPath.split('/');
        if (currentPathParams.length > 2) {
          this.$store.dispatch("intercom/track", {
            type: "Explore",
            meta: {
              category_type: currentPathParams[3],
              category: currentPathParams[currentPathParams.length - 1],
            }
          });
          console.log('Intercom New: Explore', {category_type: currentPathParams[2], category: currentPathParams[currentPathParams.length - 1]});
        }
    },
    beforeDestroy() {
        window.removeEventListener("click", this.delegatedHideInfowindows);
    },
    watch: {
        showMap(now) {
            if (now) {
                window.scrollToTarget(document.getElementById("tab-search"));
            }
        },
        "listings.timestamp"() {
            this.updateGoogleMap();
        },
        toggledInfowindow(now) {
            if (now) {
                setTimeout(() => {
                    this.toggledInfowindow = false;
                }, 150);
            }
        },
    },
    methods: {
        navigate(page) {
            this.$emit("navigate", page);
        },

        deselect(listing) {
            for (let x = 0; x < this.selected.length; x++) {
                let existing = this.selected[x];

                if (existing.id == listing.id) {
                    this.selected.splice(x, 1);
                    return;
                }
            }
        },
        select(listing) {
            let target = {
                id: listing.id,
                name: listing.space_name,
                media_id: null,
            };

            if (listing.media_id) {
                target.media_id = listing.media_id;
            } else if (listing.preferred_thumbnail) {
                target.media_id = listing.preferred_thumbnail.id;
            }

            for (let existing of this.selected) {
                if (existing.id == target.id) {
                    return;
                }
            }

            this.selected.push(target);
        },
        selectAllResults() {
            if (!this.$store.getters["user/account"]) {
                return this.$store.commit("user/changeAuthMode", "login");
            }
            if (this.listings && this.listings.all) {
                for (let listing of this.listings.all) {
                    this.select(listing);
                }
            }
            this.viewAllShortlists();
        },
        isSelected(listing) {
            return this.findInSelected(listing) !== false;
        },
        findInSelected(listing) {
            for (let x = 0; x < this.selected.length; x++) {
                if (this.selected[x].id == listing.id) {
                    return x;
                }
            }

            return false;
        },

        async updateGoogleMap() {
            // Google Maps library is loaded last in production and google object is not immediately available
            // Ref: https://github.com/olefirenko/vue-google-autocomplete/issues/39
            while (typeof google === "undefined") {
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            this.$nextTick(() => {
                if (this.listings) {
                    let bounds = new google.maps.LatLngBounds();

                    if (!this.map.googleMap) {
                        this.map.googleMap = new google.maps.Map(
                            this.$refs.gmap,
                            {
                                center: { lat: -34.397, lng: 150.644 },
                                fullscreenControl: false,
                                keyboardShortcuts: false,
                                maxZoom: 13,
                                streetViewControl: false,
                                zoom: 8,
                                styles: [
                                    {
                                        featureType: "administrative",
                                        elementType: "geometry",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType:
                                            "administrative.land_parcel",
                                        elementType: "labels",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType: "poi",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType: "poi.park",
                                        stylers: [{ visibility: "on" }],
                                    },
                                    {
                                        featureType: "road",
                                        elementType: "labels.icon",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType: "road.local",
                                        elementType: "labels",
                                        stylers: [{ visibility: "off" }],
                                    },
                                    {
                                        featureType: "transit",
                                        stylers: [{ visibility: "off" }],
                                    },
                                ],
                            }
                        );
                    }

                    // Clear existing markers
                    for (let mid in this.markers) {
                        this.markers[mid].setMap(null);
                    }
                    this.$store.commit("listings/purgeMarkers");

                    // Close any infowindows currently open
                    for (let iwid in this.infoWindows) {
                        this.infoWindows[iwid].close();
                    }
                    this.$store.commit("listings/purgeInfoWindows");

                    // Track what's been added
                    let done = {};

                    // Add our pins and infowindows to our map for our current result cards
                    for (let property of this.listings.data) {
                        if (property.lat && property.lng) {
                            this.addGoogleMapElements(property);
                            bounds.extend(
                                new google.maps.LatLng(
                                    property.lat,
                                    property.lng
                                )
                            );
                            done[property.id] = true;
                        }
                    }

                    // And whatever remains in the 'all' results
                    for (let property of this.listings.all) {
                        if (
                            property.lat &&
                            property.lng &&
                            !done[property.id]
                        ) {
                            this.addGoogleMapElements(property);
                            bounds.extend(
                                new google.maps.LatLng(
                                    property.lat,
                                    property.lng
                                )
                            );
                            done[property.id] = true;
                        }
                    }

                    if (Object.keys(this.markers).length > 0) {
                        this.map.googleMap.fitBounds(bounds);
                    }
                }
            });
        },

        addGoogleMapElements(property) {
            let image = property.preferred_thumbnail
                ? property.preferred_thumbnail.url
                : property.thumbnail_url;
            let price = "Price on application";

            let marker = new google.maps.Marker({
                position: new google.maps.LatLng(property.lat, property.lng),
                map: this.map.googleMap,
                title: property.space_name,
                icon: {
                    url: "/images/gmap-marker.png",
                    scaledSize: new google.maps.Size(28, 28),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(0, 0),
                },
            });
            this.$store.commit("listings/storeMarker", {
                id: property.id,
                marker: marker,
            });

            let infoWindow = new google.maps.InfoWindow({
                content:
                    `<a href="/location-hire/${property.address.state.slug.toLowerCase()}/${
                        property.slug
                    }">` +
                    (image
                        ? `<div class="iw-image"><img src="${image}" /></div>`
                        : "") +
                    `<div class="iw-info">` +
                    (property.address_label
                        ? `<div class="iw-address-label">${property.address_label}</div>`
                        : "") +
                    `<h3 class="iw-name">${property.space_name}</h5>
                            <h5 class="iw-price">${price}</h5>
                        </div>
                     </a>`,
            });
            this.$store.commit("listings/storeInfoWindow", {
                id: property.id,
                infoWindow: infoWindow,
            });

            google.maps.event.addListener(marker, "click", () => {
                this.toggledInfowindow = true;
                this.delegatedHideInfowindows();

                infoWindow.open(this.map.googleMap, marker);
            });
        },

        delegatedHideInfowindows(e) {
            if (
                !e ||
                (!e.target.closest(".gm-style-iw") && !this.toggledInfowindow)
            ) {
                for (let iwid in this.infoWindows) {
                    this.infoWindows[iwid].close();
                }
            }
        },

        viewAllShortlists() {
            this.$store
                .dispatch("modals/show", {
                    type: "shortlist-view-all",
                    maskCanDismiss: false,
                    headless: true,
                    narrow: true,
                    fixedFooter: true,
                    data: {
                        selected: this.selected
                    }
                })
                .then(() => {
                    this.selected = [];
                })
                .catch(() => {
                    this.selected = [];
                });
        },
    },
};
</script>
